/**
 * Theme custom scripts — rewritten from the legacy jQuery version.
 *
 * Structure:
 *   1.  Config & constants
 *   2.  Small DOM / async helpers
 *   3.  Catalogue PDF overlay
 *   4.  Mega menu hover
 *   5.  Colour swatch positioning
 *   6.  Colour swatch rendering (related products)
 *   7.  Colour swatch selection
 *   8.  Sold-out collection listing  (opt-in — see notes)
 *   9.  Storefront metafield lookup
 *  10.  Inventory / stock status
 *  11.  Zoom + drag-to-pan
 *  12.  B2B cart shine effect
 *  13.  Auto-height iframes
 *  14.  Price reveal buttons
 *  15.  Bootstrap + back-compat globals
 *
 * NOTE: the old file contained a live `shpat_…` Admin API token. It has been
 * removed. Revoke it in the Shopify admin — the endpoint it was passed to
 * (/collections/<handle>/products.json) is public and ignores it entirely.
 */
(() => {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. Config & constants
   * ------------------------------------------------------------------ */

  const SWATCH_CDN = 'https://cdn.shopify.com/s/files/1/0574/3152/3417/t/2/assets';

  /** Distance in px the swatch strip sits above the bottom of a product card. */
  const SWATCH_TOP_OFFSET = 40;

  /** Shine animation timings (ms). */
  const SHINE_INITIAL_DELAY = 5000;
  const SHINE_INTERVAL = 15000;

  /**
   * Storefront API config for metafield lookups.
   * The old value (2022-01) is long past Shopify's support window.
   * Verify the version against shopify.dev release notes before shipping.
   */
  const STOREFRONT = {
    domain: 'christina-b2b.myshopify.com',
    version: '2026-01',
    token: 'cfe2d8c03c4b734d24842840b632f1de', // public by design — keep read-only scopes
  };

  /** Customer-facing strings. Move these to locale files when convenient. */
  const STRINGS = {
    inStock: 'På lager',
    backorder: 'I restordre - Forvent længere leveringstid',
    soldOut: 'Udsolgt',
    splashSlim: 'Til slimlæder &amp; sølvarmbånd',
    splashClassic: 'Til Classic læderarmbånd',
  };

  /**
   * Colour rules, evaluated top to bottom — the first rule matching any of the
   * product's tags wins. Order matters: "Rosaforgyldt" must resolve to rosa,
   * not gold, so rosa is listed first.
   */
  const COLOR_RULES = [
    {
      group: 'cw_bicolor',
      keywords: ['bicolor'],
      swatch: 'linear-gradient(54deg, rgba(214,214,214,1) 49%, rgba(216,200,151,1) 49%)',
      image: `${SWATCH_CDN}/color_cw_bicolor.png`,
    },
    {
      group: 'cw_rosa',
      keywords: ['rosa'],
      swatch: '#f1d2c5',
      image: `${SWATCH_CDN}/color_cw_rosa.png`,
    },
    {
      group: 'cw_black',
      keywords: ['ruthenium'],
      swatch: '#666',
      image: `${SWATCH_CDN}/color_cw_black.png`,
    },
    {
      group: 'cw_silver',
      keywords: ['sølv', 'silber', 'sterling'],
      swatch: '#d6d6d6',
      image: `${SWATCH_CDN}/color_cw_silver.png`,
    },
    {
      group: 'cw_gold',
      keywords: ['forgyldt', 'vergoldet'],
      swatch: '#d8c897',
      image: `${SWATCH_CDN}/color_cw_gold.png`,
    },
  ];

  const FALLBACK_RULE = {
    group: '',
    swatch: '#d6d6d6',
    image: `${SWATCH_CDN}/color_cw_silver.png`,
  };

  /* ------------------------------------------------------------------ *
   * 2. Helpers
   * ------------------------------------------------------------------ */

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  /**
   * Delegated event binding. Replaces the old `setTimeout(…, 1200)` hacks —
   * elements added later are handled automatically.
   */
  const delegate = (root, type, selector, handler) => {
    root.addEventListener(type, (event) => {
      if (!(event.target instanceof Element)) return;
      const match = event.target.closest(selector);
      if (match && root.contains(match)) handler(event, match);
    });
  };

  const debounce = (fn, wait = 150) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  /** Tagged template that escapes every interpolated value. */
  const html = (strings, ...values) =>
    strings.reduce(
      (out, chunk, i) => out + chunk + (i < values.length ? escapeHtml(values[i]) : ''),
      ''
    );

  /** Locale-aware storefront root, e.g. "/" or "/de-de/". */
  const routeRoot = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  const priceFormatter = new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  /** @param {number} kroner amount in DKK (not øre) */
  const formatMoney = (kroner) => `${priceFormatter.format(kroner)} kr`;

  const fetchJson = async (url, options) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      ...options,
    });
    if (!response.ok) throw new Error(`${response.status} ${response.statusText} — ${url}`);
    return response.json();
  };

  /** Fetches a product from the AJAX API. Prices come back in øre. */
  const fetchProduct = (handle) =>
    fetchJson(`${routeRoot()}products/${encodeURIComponent(handle)}.js`);

  /* ------------------------------------------------------------------ *
   * 3. Catalogue PDF overlay
   * ------------------------------------------------------------------ */

  function initCatalogueOverlay() {
    delegate(document, 'click', '.catalogue-column .multicolumn-card__image-wrapper', () => {
      const container = $('.pdf-container');
      if (container) container.style.display = 'block';
    });

    delegate(document, 'click', '.close', (event, button) => {
      const parent = button.parentElement;
      if (parent) parent.style.display = 'none';
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. Mega menu hover
   * ------------------------------------------------------------------ */

  function initMegaMenuHover() {
    // Skip on touch devices — hover-to-open makes <details> impossible to close.
    if (!window.matchMedia('(hover: hover)').matches) return;

    $$('details.mega-menu').forEach((details) => {
      details.addEventListener('mouseenter', () => details.setAttribute('open', ''));
      details.addEventListener('mouseleave', () => details.removeAttribute('open'));
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. Colour swatch positioning
   * ------------------------------------------------------------------ */

  /**
   * The old version measured the *first* card on the page and applied that one
   * height to every swatch strip. This measures each card individually.
   */
  function positionColorSwatches(root = document) {
    $$('.grid__item', root).forEach((item) => {
      const inner = item.querySelector('.card__inner');
      const swatches = item.querySelectorAll('.version-two-color');
      if (!inner || !swatches.length) return;

      const top = `${inner.offsetHeight - SWATCH_TOP_OFFSET}px`;
      swatches.forEach((swatch) => swatch.style.setProperty('top', top, 'important'));
    });
  }

  /* ------------------------------------------------------------------ *
   * 6. Colour swatch rendering
   * ------------------------------------------------------------------ */

  function resolveColorRule(tags) {
    const normalized = (Array.isArray(tags) ? tags : []).map((tag) => String(tag).toLowerCase());

    for (const rule of COLOR_RULES) {
      const hit = normalized.some((tag) => rule.keywords.some((keyword) => tag.includes(keyword)));
      if (hit) return rule;
    }
    return FALLBACK_RULE;
  }

  function buildSwatch(product, { mainId, selectedHandle, useColorVersion, offerPercent }) {
    const rule = resolveColorRule(product.tags);

    const priceInOre = Number(product.price) || 0;
    const discount = offerPercent ? priceInOre * (offerPercent / 100) : 0;
    const price = (priceInOre - discount) / 100;
    const oldPrice = priceInOre / 100;

    const link = document.createElement('a');
    link.className = 'js-class js-item';
    link.classList.add(product.handle === selectedHandle ? 'selected' : 'not-selected');
    // No href: `javascript:void(0)` breaks under a strict CSP.
    link.setAttribute('role', 'button');
    link.setAttribute('tabindex', '0');

    const swatch = document.createElement(useColorVersion ? 'div' : 'img');
    swatch.className = 'color_swatch fittingbox-select';

    if (useColorVersion) {
      swatch.style.background = rule.swatch;
      swatch.setAttribute('color', rule.swatch);
    } else {
      swatch.src = rule.image;
      swatch.alt = product.title || '';
      swatch.loading = 'lazy';
    }

    Object.assign(swatch.dataset, {
      id: String(product.id ?? ''),
      mainId,
      title: product.title || '',
      url: `${routeRoot()}products/${product.handle}`,
      oldPrice: String(oldPrice),
      price: String(price),
      maxPrice: String((Number(product.price_max) || 0) / 100),
      img: product.featured_image || '',
      flipImg: product.images?.[1] || '',
      colorGroup: rule.group,
      selectedHandle: product.handle,
    });

    link.appendChild(swatch);
    return link;
  }

  async function renderCardSwatches(card) {
    const container = card.querySelector('.color-selection');
    if (!container || container.dataset.swatchesRendered === 'true') return;
    container.dataset.swatchesRendered = 'true';

    const selectedHandle = (container.dataset.selectedHandle || '').trim();
    const related = container.dataset.relatedHandles || '';

    // Legacy truthiness: any non-empty attribute value counts as "on",
    // including the literal string "false". Tighten once the Liquid is checked.
    const useColorVersion = Boolean(container.dataset.colorVersionTwo);
    const mainId = container.dataset.mainId || '';
    const offerPercent = Number(card.dataset.offerNumber) || 0;

    const handles = [...new Set(
      [selectedHandle, ...related.split('|')].map((h) => h.trim()).filter(Boolean)
    )];
    if (!handles.length) return;

    // Promise.all preserves input order, so swatch order is now stable across
    // page loads — the old push-on-completion approach shuffled them.
    const results = await Promise.all(
      handles.map((handle) => fetchProduct(handle).catch(() => null))
    );

    const fragment = document.createDocumentFragment();
    results.filter(Boolean).forEach((product) => {
      fragment.appendChild(
        buildSwatch(product, { mainId, selectedHandle, useColorVersion, offerPercent })
      );
    });

    if (fragment.childElementCount) container.appendChild(fragment);
  }

  async function renderReferenceProducts(root = document) {
    const cards = $$('li.grid__item.custom__grid', root);
    if (!cards.length) return;

    await Promise.all(cards.map((card) => renderCardSwatches(card)));
    positionColorSwatches(root);
  }

  /* ------------------------------------------------------------------ *
   * 7. Colour swatch selection
   * ------------------------------------------------------------------ */

  function applySwatchSelection(swatch) {
    const card = swatch.closest('li.grid__item.custom__grid');
    if (!card) return;

    const { url, img, price, oldPrice } = swatch.dataset;

    const heading = card.querySelector('.card__content .card__heading a');
    if (heading && url) heading.setAttribute('href', url);

    const image = card.querySelector('.card__media .main-image');
    if (image && img) {
      image.src = img;
      image.srcset = img;
    }

    const anchor = swatch.closest('a');
    const list = anchor?.parentElement;
    if (anchor && list) {
      list.querySelectorAll('a.selected').forEach((el) => el.classList.remove('selected'));
      anchor.classList.add('selected');
    }

    const regular = card.querySelector('.card-information .price-item--regular');
    if (regular && price !== undefined) regular.textContent = formatMoney(Number(price));

    // Only render an old price when there actually is one — the legacy code
    // printed "undefined kr" on every non-sale product.
    const previous = card.querySelector('.card-information .price-item--old');
    if (previous) {
      const value = Number(oldPrice);
      previous.textContent = Number.isFinite(value) && value > 0 ? formatMoney(value) : '';
    }
  }

  function initSwatchSelection() {
    const selector = '.color-selection.collection a img, .color-selection.collection .color_swatch';

    delegate(document, 'click', selector, (event, swatch) => {
      event.preventDefault();
      applySwatchSelection(swatch);
    });

    delegate(document, 'keydown', '.color-selection.collection a.js-item', (event, anchor) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const swatch = anchor.querySelector('.color_swatch');
      if (!swatch) return;
      event.preventDefault();
      applySwatchSelection(swatch);
    });
  }

  /* ------------------------------------------------------------------ *
   * 8. Sold-out collection listing (opt-in)
   * ------------------------------------------------------------------ *
   * Kept for parity with the legacy file, but this really belongs in Liquid:
   * it downloads the entire collection client-side just to append markup the
   * theme already knows how to render. Call CDLTheme.renderSoldOutProducts()
   * to enable it.
   */

  const SOLD_OUT_PAGE_SIZE = 250;

  async function fetchCollectionProducts(path, page = 1, accumulated = []) {
    const url = `${path.replace(/\/$/, '')}/products.json?limit=${SOLD_OUT_PAGE_SIZE}&page=${page}`;
    const payload = await fetchJson(url);
    const products = accumulated.concat(payload.products || []);

    if ((payload.products || []).length === SOLD_OUT_PAGE_SIZE) {
      return fetchCollectionProducts(path, page + 1, products);
    }
    return products;
  }

  function buildSoldOutCard(product) {
    const tags = product.tags || [];
    const type = product.product_type;
    const image = product.images?.[0]?.src || '';
    const price = product.variants?.[0]?.price ?? '';
    const url = `${routeRoot()}products/${product.handle}`;

    let splash = '';
    if (type === 'Charms' && tags.includes('4 mm')) {
      splash =
        '<div class="grid-product__tag grid-product__tag--plus-size first-splash splash-design-round" ' +
        'style="background-color:#a3a1a1;color:#fff;font-size:12px;">' +
        `<p>${STRINGS.splashSlim}</p></div>`;
    } else if (type === 'Charms' && tags.includes('6 mm')) {
      splash =
        '<div class="grid-product__tag grid-product__tag--second-tag second-splash splash-design-round move-down" ' +
        'style="background-color:#000;color:#fff;font-size:12px;">' +
        `<p>${STRINGS.splashClassic}</p></div>`;
    }

    return html`
      <li class="grid__item custom__grid" data-handle="${product.handle}">
        <div class="card-wrapper underline-links-hover">
          <div class="card card--standard card--media" style="--ratio-percent: 100%;">
            <div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: 100%;">
              ${'__SPLASH__'}
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  <img src="${image}" alt="${product.title}" class="motion-reduce main-image"
                       width="2000" height="2000" loading="lazy">
                </div>
              </div>
              <div class="card__badge top right">
                <span class="badge font-weight-500 color-grey">${STRINGS.soldOut}</span>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${url}" class="full-unstyled-link">${product.title}</a>
                </h3>
                <div class="card-information">
                  <div class="price price--sold-out">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="price-item price-item--regular">${price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `.replace('__SPLASH__', splash);
  }

  async function renderSoldOutProducts() {
    const container = $('#sold-out-collection');
    if (!container) return;

    // Only render on the final pagination page.
    const currentPage = new URLSearchParams(window.location.search).get('page');
    const maxPage = container.dataset.pagesize;
    if (maxPage !== '1' && currentPage !== maxPage) return;

    try {
      const products = await fetchCollectionProducts(window.location.pathname);

      // One card per product — the legacy loop emitted a duplicate card for
      // every unavailable variant.
      const markup = products
        .filter((product) => (product.variants || []).some((v) => v.available === false))
        .map(buildSoldOutCard)
        .join('');

      container.insertAdjacentHTML('beforeend', markup);
      positionColorSwatches(container);
    } catch (error) {
      console.error('[theme] sold-out listing failed', error);
    }
  }

  /* ------------------------------------------------------------------ *
   * 9. Storefront metafield lookup
   * ------------------------------------------------------------------ */

  async function getMetafieldValue(handle, namespace, key) {
    const query = `
      query ProductMetafield($handle: String!, $namespace: String!, $key: String!) {
        productByHandle(handle: $handle) {
          title
          metafield(namespace: $namespace, key: $key) { value }
        }
      }
    `;

    const payload = await fetchJson(
      `https://${STOREFRONT.domain}/api/${STOREFRONT.version}/graphql.json`,
      {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT.token,
        },
        body: JSON.stringify({ query, variables: { handle, namespace, key } }),
      }
    );

    if (payload.errors?.length) throw new Error(payload.errors[0].message);
    return payload.data?.productByHandle?.metafield?.value ?? null;
  }

  /* ------------------------------------------------------------------ *
   * 10. Inventory / stock status
   * ------------------------------------------------------------------ */

  const getInventoryEntry = (productId, variantId) =>
    window.inventories?.[productId]?.[variantId] ?? null;

  const isInStock = (productId, variantId) => {
    const entry = getInventoryEntry(productId, variantId);
    return Boolean(entry && Number(entry.quantity) > 0);
  };

  /** Reads the currently selected option values, in option order. */
  function getSelectedOptionValues() {
    const radios = $$('variant-radios fieldset')
      .map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value)
      .filter((value) => value !== undefined);
    if (radios.length) return radios;

    return $$('variant-selects select').map((select) => select.value);
  }

  /** Matches on the full option tuple, not "any option happens to equal X". */
  function findVariant(product, values) {
    if (!values.length) return null;
    return (
      product.variants.find((variant) =>
        values.every((value, index) => String(variant.options[index]) === String(value))
      ) || null
    );
  }

  let cachedProduct = null;

  async function updateInventoryStatus() {
    const info = $('.product__info-container');
    const output = $('#inventory-element');
    if (!info || !output) return;

    const handle = info.dataset.productHandle;
    if (!handle) return;

    try {
      cachedProduct = cachedProduct || (await fetchProduct(handle));
    } catch (error) {
      console.error('[theme] could not load product for inventory status', error);
      return;
    }

    const onlyDefaultVariant = info.dataset.onlyDefault === 'true';
    const variant = onlyDefaultVariant
      ? null
      : findVariant(cachedProduct, getSelectedOptionValues());
    const variantId = variant?.id ?? info.dataset.id;

    const dot = output.querySelector('.inventory-color');
    const label = output.querySelector('.inventory-text');
    const inStock = isInStock(cachedProduct.id, variantId);

    dot?.classList.toggle('in-stock', inStock);
    dot?.classList.toggle('out-of-stock', !inStock);
    if (label) label.textContent = inStock ? STRINGS.inStock : STRINGS.backorder;
  }

  function initInventoryStatus() {
    if (!$('.product__info-container') || !$('#inventory-element')) return;

    updateInventoryStatus();
    delegate(document, 'change', '.product-form__input input[type="radio"]', updateInventoryStatus);
    delegate(document, 'change', 'variant-selects select', updateInventoryStatus);
  }

  /* ------------------------------------------------------------------ *
   * 11. Zoom + drag-to-pan
   * ------------------------------------------------------------------ */

  /**
   * `set_draggable()` used to reference `resultBlock`, which was scoped to
   * another function — drag-to-pan threw a ReferenceError and never worked.
   */
  function enableDragPan(element) {
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    element.addEventListener('pointerdown', (event) => {
      if (!element.classList.contains('draggable')) return;
      dragging = true;
      startX = event.clientX;
      startY = event.clientY;
      scrollLeft = element.scrollLeft;
      scrollTop = element.scrollTop;
      element.setPointerCapture(event.pointerId);
      element.style.cursor = 'grabbing';
    });

    element.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      element.scrollLeft = scrollLeft + (startX - event.clientX);
      element.scrollTop = scrollTop + (startY - event.clientY);
    });

    const stop = (event) => {
      if (!dragging) return;
      dragging = false;
      element.releasePointerCapture?.(event.pointerId);
      element.style.cursor = '';
    };

    element.addEventListener('pointerup', stop);
    element.addEventListener('pointercancel', stop);
  }

  function initZoom() {
    const zoomItem = $('#zoom-item');
    if (zoomItem) {
      zoomItem.addEventListener('click', () => {
        const container = zoomItem.querySelector('.container');
        if (!container) return;
        const shown = container.classList.toggle('show-zoom');
        container.classList.toggle('hide-zoom', !shown);
      });
    }

    const range = $('#zoom-range');
    const result = $('#resultBlock');
    if (!range || !result) return;

    range.addEventListener('input', (event) => {
      const scale = Number(event.target.value) || 1;
      result.style.transform = `scale(${scale})`;
      result.classList.toggle('draggable', scale > 1);
    });

    enableDragPan(result);
  }

  /* ------------------------------------------------------------------ *
   * 12. B2B cart shine effect
   * ------------------------------------------------------------------ */

  function initShineEffect() {
    const element = $('#b2b-cart');
    if (!element) return;

    element.addEventListener('animationend', () => {
      element.style.animation = 'none';
    });

    const play = () => {
      if (document.hidden) return; // don't burn cycles in a background tab
      element.style.animation = 'none';
      void element.offsetWidth; // force reflow so the animation restarts
      element.style.animation = 'shine-animation 2s forwards';
    };

    setTimeout(play, SHINE_INITIAL_DELAY);
    setInterval(play, SHINE_INTERVAL);
  }

  /* ------------------------------------------------------------------ *
   * 13. Auto-height iframes
   * ------------------------------------------------------------------ */

  function initAutoHeightIframes() {
    const resize = (frame) => {
      try {
        const body = frame.contentDocument?.body;
        if (body) frame.style.height = `${body.scrollHeight}px`;
      } catch {
        // Cross-origin iframe — nothing we can read.
      }
    };

    $$('.iframe-full-height').forEach((frame) => {
      frame.addEventListener('load', () => resize(frame));
      resize(frame); // in case it loaded before this script ran
    });
  }

  /* ------------------------------------------------------------------ *
   * 14. Price reveal buttons
   * ------------------------------------------------------------------ */

  function initPriceReveal() {
    delegate(document, 'click', '.button.show-this-price', (event, button) => {
      const target = button
        .closest('.price__container')
        ?.querySelector('.invisible-price-container');
      target?.classList.add('show');
    });

    delegate(document, 'click', '#show-all-prices', () => {
      $$('.invisible-price-container').forEach((el) => el.classList.add('show'));
    });
  }

  /* ------------------------------------------------------------------ *
   * 15. Bootstrap
   * ------------------------------------------------------------------ */

  function init() {
    initCatalogueOverlay();
    initMegaMenuHover();
    initSwatchSelection();
    initPriceReveal();
    initZoom();
    initShineEffect();
    initAutoHeightIframes();
    initInventoryStatus();

    renderReferenceProducts().catch((error) =>
      console.error('[theme] swatch rendering failed', error)
    );

    positionColorSwatches();
    window.addEventListener('load', () => positionColorSwatches());
    window.addEventListener('resize', debounce(() => positionColorSwatches(), 150));
  }

  ready(init);

  /**
   * Public API. The `window.*` aliases exist only so the inline scripts still
   * sitting in main-product.liquid keep working — remove them once that Liquid
   * is cleaned up.
   */
  window.CDLTheme = {
    renderReferenceProducts,
    renderSoldOutProducts,
    positionColorSwatches,
    updateInventoryStatus,
    getMetafieldValue,
    isInStock,
    getInventoryEntry,
  };

  window.render_reference_products = renderReferenceProducts;
  window.get_metafield_value = getMetafieldValue;
  window.product_inventory_functionality = () => updateInventoryStatus();
  window.check_stock_status = isInStock;
  window.display_inventory_data = (productId, variantId) =>
    console.log(getInventoryEntry(productId, variantId));
})();